/**
 * Three things a coach came here to do.
 *
 * The home page used to lead with "How do you want to get better today?" over
 * four cards reading Team Coaching, Player Development, Community and Plans.
 * Those are categories, so every pick led to another pick: the largest element
 * on the page was a fork into more forks, which is the decision fatigue the
 * rest of this rebuild is about.
 *
 * So: three groups, and the thing you actually arrive at is the item, not the
 * group. "Build a plan" is one tap from the home page rather than two.
 *
 * Learn / Coach / Talk shop, and the shape is 3 / 3 / 1 on purpose. Talk shop
 * is one door because it is one place; the card carries what is happening
 * there instead of a list.
 *
 * Pure and import-free so the test can walk it against NAV without pulling
 * mockData into anything. A `to` that is not a real nav id fails the build,
 * which is the only way a door onto nothing gets caught: it looks completely
 * fine in a diff.
 *
 * No em dashes.
 */

export const ACTION_PATHS = [
  {
    id: "learn",
    label: "Learn",
    // A face, each. The first photographs were recovered from the old cards'
    // base64 at 267x400 and stretched to four times that, which is why they
    // were grainy. These are the landing page's full size shoots, cropped to
    // a wide band around the faces, so they are sharp and the crop is
    // already right; artPosition stays for a photo that needs nudging.
    art: "/images/paths/learn.jpg",
    artPosition: "center 50%",
    icon: "learn",
    blurb: "Sharpen how you think about the game.",
    items: [
      { id: "lab", label: "Learning Lab", to: "learn" },
      { id: "master", label: "Masterclasses", to: "master" },
      // One door onto the drill libraries. Today it opens the biggest of
      // them, which has its own search; a true cross-library door is
      // DrillSearchPanel, which currently only exists inside the plan
      // builder. Pointing at a library a coach can search beats pointing at
      // a picker they cannot reach.
      { id: "drills", label: "Find a drill", to: "pd" },
    ],
  },
  {
    id: "coach",
    label: "Coach",
    art: "/images/paths/coach.jpg",
    artPosition: "center 50%",
    icon: "coach",
    blurb: "Get ready for your next session.",
    items: [
      { id: "plans", label: "Build a plan", to: "plans" },
      { id: "players", label: "Players and team", to: "dev-studio" },
      { id: "playbook", label: "My playbook", to: "playbook" },
    ],
  },
  {
    id: "talk",
    label: "Talk shop",
    art: "/images/paths/talk.jpg",
    artPosition: "center 50%",
    icon: "talk",
    blurb: "See what other coaches are working on.",
    items: [
      { id: "community", label: "Community", to: "community" },
    ],
  },
];

export const PATH_IDS = ACTION_PATHS.map(p => p.id);

/** Every destination this page offers, for the test and for analytics. */
export const ALL_PATH_TARGETS = ACTION_PATHS.flatMap(p => p.items.map(i => i.to));

/** The group a coach was last in, falling back to the first. */
export function resolvePath(id) {
  return ACTION_PATHS.find(p => p.id === id) || ACTION_PATHS[0];
}
